import { useEffect, useRef, useState } from "react";
import { MapPin, LocateFixed } from "lucide-react";
import { getGoogleMapsApiKey, loadGoogleMaps } from "@/lib/googleMaps";
import { TextArea } from "@/components/ui/textarea";
import { composePlaceAddress, legLabel, LegType } from "@/features/delivery/lib/deliveryPresentation.js";

export interface DeliveryAddressValue {
  address: string;
  lat: string;
  lng: string;
  remark: string;
}

interface DeliveryAddressInputProps {
  value: DeliveryAddressValue;
  onChange: (value: DeliveryAddressValue) => void;
  type?: LegType;
}

/**
 * Address + coordinates input for the delivery destination.
 *
 * When a Google Maps key is configured (VITE_GOOGLE_MAPS_API_KEY) the address
 * field becomes a Places autocomplete that fills the coordinates automatically.
 * Without a key it degrades to manual coordinate entry (still fully functional),
 * and the browser Geolocation "use my location" button remains as a shortcut.
 */
export default function DeliveryAddressInput({ value, onChange, type = "pickup" }: DeliveryAddressInputProps) {
  const addressLabel = `${legLabel(type)} address`;
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [autocompleteUnavailable, setAutocompleteUnavailable] = useState(false);
  const addressRef = useRef<HTMLInputElement>(null);
  const hasGoogle = Boolean(getGoogleMapsApiKey());
  // Keep the latest value in a ref so the Google listener always reads fresh state.
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!hasGoogle || !addressRef.current) {
      return;
    }

    let autocomplete: any;
    let cancelled = false;

    // Google calls this global if the key is rejected (bad referrer, billing
    // off, API not enabled, etc.). Fall back to manual entry with a clear hint.
    (window as any).gm_authFailure = () => setAutocompleteUnavailable(true);

    loadGoogleMaps().then((google) => {
      if (cancelled || !google?.maps?.places || !addressRef.current) {
        if (!cancelled && !google) {
          setAutocompleteUnavailable(true);
        }
        return;
      }

      autocomplete = new google.maps.places.Autocomplete(addressRef.current, {
        fields: ["name", "types", "formatted_address", "geometry"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const loc = place?.geometry?.location;
        if (!loc) {
          return;
        }
        onChange({
          ...valueRef.current,
          address: composePlaceAddress(place) || valueRef.current.address,
          lat: loc.lat().toFixed(7),
          lng: loc.lng().toFixed(7),
        });
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGoogle]);

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) {
      setGeoError("Location is not supported on this device.");
      return;
    }

    setLocating(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onChange({
          ...valueRef.current,
          lat: position.coords.latitude.toFixed(7),
          lng: position.coords.longitude.toFixed(7),
        });
        setLocating(false);
      },
      () => {
        setGeoError("We couldn't get your location. Please enter it manually.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <label className="text-sm md:col-span-2">
        <span className="mb-1 block font-medium">{addressLabel}</span>
        <div className="flex items-center rounded-md border border-neutral-300 px-3">
          <MapPin className="mr-2 h-4 w-4 text-neutral-500" aria-hidden="true" />
          <input
            ref={addressRef}
            value={value.address}
            onChange={(event) => onChange({ ...value, address: event.target.value })}
            className="h-10 flex-1 outline-none"
            placeholder={hasGoogle ? "Start typing your address…" : `Enter the ${addressLabel.toLowerCase()}`}
            aria-label={addressLabel}
          />
        </div>
        {hasGoogle && !autocompleteUnavailable && (
          <span className="mt-1 block text-xs text-neutral-500">
            Pick a suggestion and we'll fill your location automatically.
          </span>
        )}
        {autocompleteUnavailable && (
          <span className="mt-1 block text-xs text-amber-600">
            Address suggestions are unavailable right now — please enter your address and set the
            latitude/longitude below (or use “Use my current location”).
          </span>
        )}
      </label>

      <label className="text-sm md:col-span-2">
        <span className="mb-1 block font-medium">Remark (optional)</span>
        <TextArea
          id="delivery-remark"
          placeholder="I.e. Opposite side of the hotel."
          value={value.remark}
          onChange={(event) =>
            onChange({ ...value, remark: (event.target as HTMLTextAreaElement).value })
          }
          className="min-h-[72px]"
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium">Latitude</span>
        <input
          value={value.lat}
          onChange={(event) => onChange({ ...value, lat: event.target.value })}
          className="h-10 w-full rounded-md border border-neutral-300 px-3"
          inputMode="decimal"
          aria-label="Latitude"
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block font-medium">Longitude</span>
        <input
          value={value.lng}
          onChange={(event) => onChange({ ...value, lng: event.target.value })}
          className="h-10 w-full rounded-md border border-neutral-300 px-3"
          inputMode="decimal"
          aria-label="Longitude"
        />
      </label>

      <div className="md:col-span-2">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="inline-flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-2 text-sm hover:bg-neutral-50 disabled:opacity-60"
        >
          <LocateFixed className="h-4 w-4" aria-hidden="true" />
          {locating ? "Locating…" : "Use my current location"}
        </button>
        {geoError && <p className="mt-2 text-sm text-red-600">{geoError}</p>}
      </div>
    </div>
  );
}
